import { useCallback, useState } from 'react';
import Cookies from 'universal-cookie';
import type { CookieSetOptions } from 'universal-cookie/es6/types';

function getCookies(cookie?: string): Cookies {
  return new Cookies(cookie);
}

/**
 * Hook for working with cookies. This is a copy of the useCookie hook from the `react-use` library that
 * was updated to use the `universal-cookie` library instead of the `js-cookie` library.
 *
 * @param cookieName the name of the cookie
 *
 * @author Adam Ayres
 */
const useCookie = (
  cookieName: string
): [string | null, (newValue: string, options?: CookieSetOptions) => void, () => void] => {
  const [value, setValue] = useState<string | null>(() => getCookies().get(cookieName) || null);

  const updateCookie = useCallback(
    (newValue: string, options?: CookieSetOptions) => {
      getCookies().set(cookieName, newValue, options);
      setValue(newValue);
    },
    [cookieName]
  );

  const deleteCookie = useCallback(() => {
    getCookies().remove(cookieName);
    setValue(null);
  }, [cookieName]);

  return [value, updateCookie, deleteCookie];
};

export default useCookie;
